// Recruitment / CV screening data layer.
//
// Firestore collections owned by this module:
//   jobOpenings  — { branch }_{ slug(jobTitle) }_{ timestamp }
//   candidates   — { branch }_{ jobOpeningId }_{ hashString(fullName+email) }
//
// Both are branch-scoped ('limpopo' | 'pretoria'). Admin
// (ADMIN_EMAILS) sees all branches; non-admin users see only the
// branch passed to getJobOpenings / getCandidates.
//
// Architecture mirrors bankStore.js:
//   - Firestore (REST API) is source of truth
//   - localStorage is a per-branch, per-collection speed cache
//   - deterministic-ID PATCHes everywhere — re-running the same
//     analysis overwrites the same doc instead of duplicating
//   - reuses hashString (DJB2 → base36) and slugify from bankStore

import * as Sentry from '@sentry/react'
import { restSet, restDelete, restQuery } from './firestoreRest'
import { hashString, slugify } from './bankStore'

// Error handling helpers — see bankStore.js for the rationale.
async function restSetSafe(path, record, context) {
  try {
    await restSet(path, record)
    return record
  } catch (err) {
    console.warn('[recruitmentStore]', context.fn, 'failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'recruitmentStore', ...context } })
    throw err
  }
}

async function restDeleteSafe(path, context) {
  try {
    await restDelete(path)
    return true
  } catch (err) {
    console.warn('[recruitmentStore]', context.fn, 'failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'recruitmentStore', ...context } })
    throw err
  }
}

export { hashString, slugify }

// ─── Constants ────────────────────────────────────────────────────

export const BRANCHES = ['limpopo', 'pretoria']
export const DEPARTMENTS = ['Kitchen', 'Bakery', 'Tuck Shop', 'Admin', 'Other']

export const CANDIDATE_STATUSES = [
  'pending', 'shortlisted', 'rejected', 'interviewed', 'hired',
]

export const JOB_STATUSES = ['open', 'closed', 'filled']

/** Preferred local languages per branch. Used by the scoring
 *  endpoint to award the language bonus. Exported so the API
 *  handler can see it without duplicating the list. */
export const BRANCH_LOCAL_LANGUAGES = {
  limpopo: ['tshivenda', 'venda', 'sepedi', 'pedi', 'northern sotho'],
  pretoria: ['afrikaans', 'setswana', 'tswana', 'zulu', 'sotho'],
}

// ─── Deterministic ID builders ────────────────────────────────────

/** {branch}_{slug(jobTitle)}_{timestamp}. `timestamp` is optional —
 *  if omitted, Date.now() is used (non-deterministic across calls,
 *  but deterministic once the opening exists). */
export function jobOpeningId(branch, jobTitle, timestamp) {
  const ts = timestamp || Date.now()
  const slug = slugify(jobTitle) || 'untitled'
  return `${String(branch || '').toLowerCase()}_${slug}_${ts}`
}

/** {branch}_{jobOpeningId}_{hashString(fullName+email)}.
 *  Same fullName + email on the same job opening → same ID →
 *  duplicate detection is automatic at the Firestore level. */
export function candidateId(branch, openingId, fullName, email) {
  const key = `${String(fullName || '').toLowerCase().trim()}|${String(email || '').toLowerCase().trim()}`
  return `${String(branch || '').toLowerCase()}_${openingId}_${hashString(key)}`
}

// ─── localStorage cache helpers ───────────────────────────────────

const LS_PREFIX = 'desres_recruit_'

function lsKey(collection, branch) { return `${LS_PREFIX}${collection}_${branch}` }

function lsLoad(collection, branch) {
  try { return JSON.parse(localStorage.getItem(lsKey(collection, branch)) || '[]') }
  catch { return [] }
}

function lsSave(collection, branch, list) {
  try { localStorage.setItem(lsKey(collection, branch), JSON.stringify(list)) }
  catch { /* quota / serialization — ignore */ }
}

function upsertLocal(collection, branch, record) {
  const list = lsLoad(collection, branch)
  const idx = list.findIndex(r => r.id === record.id)
  if (idx >= 0) list[idx] = record
  else list.push(record)
  lsSave(collection, branch, list)
}

function removeLocal(collection, branch, id) {
  const list = lsLoad(collection, branch).filter(r => r.id !== id)
  lsSave(collection, branch, list)
}

// ─── jobOpenings CRUD ─────────────────────────────────────────────

export function getJobOpenings(branch, { status } = {}) {
  let list = lsLoad('jobOpenings', branch)
  if (status) list = list.filter(j => j.status === status)
  return list
}

export async function refreshJobOpenings(branch) {
  try {
    const list = await restQuery('jobOpenings', 'branch', branch)
    lsSave('jobOpenings', branch, list)
    return list
  } catch (err) {
    console.warn('[recruitmentStore] refreshJobOpenings failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'recruitmentStore', fn: 'refreshJobOpenings', branch } })
    return lsLoad('jobOpenings', branch)
  }
}

/** Save a new or updated job opening. New openings get a freshly
 *  generated ID; existing openings must pass `id` in the payload to
 *  preserve it. */
export async function saveJobOpening(opening) {
  const {
    id: existingId, branch, jobTitle, department, jobDescription,
    requiredSkills, niceToHaveSkills,
    preferredAgeMin, preferredAgeMax, preferredLocation,
    minExperienceYears, minEducation,
    status,
  } = opening
  if (!branch) throw new Error('branch is required')
  if (!BRANCHES.includes(branch)) throw new Error(`branch must be one of ${BRANCHES.join(', ')}`)
  if (!jobTitle) throw new Error('jobTitle is required')

  const id = existingId || jobOpeningId(branch, jobTitle)
  // Preserve createdAt across updates — look up the cached doc.
  const cached = getJobOpenings(branch).find(j => j.id === id)
  const record = {
    id,
    branch,
    jobTitle,
    department: department || 'Other',
    jobDescription: jobDescription || '',
    requiredSkills: Array.isArray(requiredSkills) ? requiredSkills : [],
    niceToHaveSkills: Array.isArray(niceToHaveSkills) ? niceToHaveSkills : [],
    preferredAgeMin: Number(preferredAgeMin) || null,
    preferredAgeMax: Number(preferredAgeMax) || null,
    preferredLocation: preferredLocation || '',
    minExperienceYears: Number(minExperienceYears) || 0,
    minEducation: minEducation || '',
    status: status || cached?.status || 'open',
    createdAt: cached?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    closedAt: cached?.closedAt || null,
  }

  await restSetSafe(`jobOpenings/${id}`, record, { fn: 'saveJobOpening', id, branch, jobTitle })
  upsertLocal('jobOpenings', branch, record)
  return record
}

/** Shallow-merge update — preserves every field not in `updates`. */
export async function updateJobOpening(id, branch, updates) {
  const existing = getJobOpenings(branch).find(j => j.id === id)
  if (!existing) return null
  const merged = {
    ...existing,
    ...updates,
    id,
    branch,
    updatedAt: new Date().toISOString(),
  }
  await restSetSafe(`jobOpenings/${id}`, merged, { fn: 'updateJobOpening', id, branch })
  upsertLocal('jobOpenings', branch, merged)
  return merged
}

/** Flip status to 'closed' and stamp closedAt. Keeps the record
 *  (and all its candidates) for historical reference. */
export async function closeJobOpening(id, branch) {
  return updateJobOpening(id, branch, { status: 'closed', closedAt: new Date().toISOString() })
}

export async function deleteJobOpening(id, branch) {
  await restDeleteSafe(`jobOpenings/${id}`, { fn: 'deleteJobOpening', id, branch })
  removeLocal('jobOpenings', branch, id)
  return true
}

// ─── candidates CRUD ──────────────────────────────────────────────

/** Filters:
 *    jobOpeningId — limit to one opening
 *    shortlisted  — only those marked shortlisted
 *    status       — 'pending' | 'shortlisted' | 'rejected' | ...
 *  Returns candidates sorted by rankingScore descending. */
export function getCandidates(branch, { jobOpeningId: jobId, shortlisted, status } = {}) {
  let list = lsLoad('candidates', branch)
  if (jobId) list = list.filter(c => c.jobOpeningId === jobId)
  if (typeof shortlisted === 'boolean') list = list.filter(c => !!c.shortlisted === shortlisted)
  if (status) list = list.filter(c => c.status === status)
  return list.slice().sort((a, b) => (b.rankingScore || 0) - (a.rankingScore || 0))
}

export async function refreshCandidates(branch) {
  try {
    const list = await restQuery('candidates', 'branch', branch)
    lsSave('candidates', branch, list)
    return list
  } catch (err) {
    console.warn('[recruitmentStore] refreshCandidates failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'recruitmentStore', fn: 'refreshCandidates', branch } })
    return lsLoad('candidates', branch)
  }
}

/** Check for an existing candidate with the same fullName + email
 *  on the same opening. Returns the cached record or null. Used by
 *  the upload flow to skip duplicate CVs. */
export function findExistingCandidate(branch, openingId, fullName, email) {
  if (!branch || !openingId) return null
  const id = candidateId(branch, openingId, fullName, email)
  return getCandidates(branch, { jobOpeningId: openingId }).find(c => c.id === id) || null
}

/** Save a candidate record. The ID is derived from branch + opening
 *  + fullName + email, so re-saving the same person on the same
 *  opening overwrites the doc. */
export async function saveCandidate(candidate) {
  const {
    branch, jobOpeningId: openingId,
    firstName, lastName, fullName, email, phone,
    location, age, dateOfBirth,
    education, experience, skills, languages, certifications,
    cvText, cvFileName,
    rankingScore, rankingBreakdown, rankingReason,
    strengths, weaknesses, recommendation,
    shortlisted, status, notes,
    analysisFailed, analysisError,
  } = candidate

  if (!branch) throw new Error('branch is required')
  if (!openingId) throw new Error('jobOpeningId is required')
  if (!fullName && !(firstName || lastName)) throw new Error('fullName (or first+last) is required')

  const resolvedName = fullName || `${firstName || ''} ${lastName || ''}`.trim()
  const id = candidateId(branch, openingId, resolvedName, email)
  const cached = getCandidates(branch, { jobOpeningId: openingId }).find(c => c.id === id)

  const record = {
    id,
    branch,
    jobOpeningId: openingId,
    firstName: firstName || '',
    lastName: lastName || '',
    fullName: resolvedName,
    email: email || '',
    phone: phone || '',
    location: location || '',
    age: age ?? null,
    dateOfBirth: dateOfBirth || null,
    education: Array.isArray(education) ? education : [],
    experience: Array.isArray(experience) ? experience : [],
    skills: Array.isArray(skills) ? skills : [],
    languages: Array.isArray(languages) ? languages : [],
    certifications: Array.isArray(certifications) ? certifications : [],
    cvText: cvText || cached?.cvText || '',
    cvFileName: cvFileName || cached?.cvFileName || '',
    rankingScore: Number(rankingScore) || 0,
    rankingBreakdown: rankingBreakdown || null,
    rankingReason: rankingReason || '',
    strengths: Array.isArray(strengths) ? strengths : [],
    weaknesses: Array.isArray(weaknesses) ? weaknesses : [],
    recommendation: recommendation || '',
    shortlisted: !!shortlisted,
    status: status || cached?.status || 'pending',
    notes: notes != null ? notes : (cached?.notes || ''),
    analysisFailed: !!analysisFailed,
    analysisError: analysisError || '',
    createdAt: cached?.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }

  await restSetSafe(`candidates/${id}`, record, { fn: 'saveCandidate', id, branch, jobOpeningId: openingId })
  upsertLocal('candidates', branch, record)
  return record
}

/** Shallow-merge update — typically used to change status,
 *  shortlisted flag, or recruiter notes. */
export async function updateCandidate(id, branch, updates) {
  const existing = lsLoad('candidates', branch).find(c => c.id === id)
  if (!existing) return null
  const merged = {
    ...existing,
    ...updates,
    id,
    branch,
    updatedAt: new Date().toISOString(),
  }
  await restSetSafe(`candidates/${id}`, merged, { fn: 'updateCandidate', id, branch })
  upsertLocal('candidates', branch, merged)
  return merged
}

export async function deleteCandidate(id, branch) {
  await restDeleteSafe(`candidates/${id}`, { fn: 'deleteCandidate', id, branch })
  removeLocal('candidates', branch, id)
  return true
}

/** Bulk re-score helper. Accepts a list of candidate IDs and an
 *  async scoring function `scoreFn(candidate) → { rankingScore,
 *  rankingBreakdown, rankingReason, strengths, weaknesses,
 *  recommendation }`. The caller is responsible for rate limiting;
 *  this helper processes sequentially with the provided delay. */
export async function rescoreCandidates(branch, candidateIds, scoreFn, { delayMs = 500, onProgress } = {}) {
  let done = 0, failed = 0
  for (const id of candidateIds) {
    const existing = lsLoad('candidates', branch).find(c => c.id === id)
    if (!existing) { done++; onProgress?.({ id, done, failed, total: candidateIds.length }); continue }
    try {
      const score = await scoreFn(existing)
      await updateCandidate(id, branch, score)
      done++
    } catch (err) {
      console.warn('[recruitmentStore] rescoreCandidates failed for', id, err?.message)
      failed++
    }
    onProgress?.({ id, done, failed, total: candidateIds.length })
    if (delayMs > 0) await new Promise(resolve => setTimeout(resolve, delayMs))
  }
  return { done, failed, total: candidateIds.length }
}

// ─── Aggregate helpers for UI ─────────────────────────────────────

/** For each job opening, attach summary stats (candidateCount,
 *  shortlistedCount, topScore). Pure computation — no IO. */
export function summariseOpenings(openings, candidates) {
  const byOpening = new Map()
  for (const c of (candidates || [])) {
    const list = byOpening.get(c.jobOpeningId) || []
    list.push(c)
    byOpening.set(c.jobOpeningId, list)
  }
  return (openings || []).map(o => {
    const cands = byOpening.get(o.id) || []
    const sorted = [...cands].sort((a, b) => (b.rankingScore || 0) - (a.rankingScore || 0))
    const top = sorted[0]
    return {
      ...o,
      candidateCount: cands.length,
      shortlistedCount: cands.filter(c => c.shortlisted).length,
      topScore: top?.rankingScore || 0,
      topCandidateName: top?.fullName || '',
    }
  })
}
